// @@RECEIPT_REQUESTS_v122@@ Receipts › "Looking for" — receipts & invoices we still need.
// Date, notes, paid from, amount. Anyone internal can add/edit/mark found;
// admin/manager can delete. "Print list" prints the current tab as a clean
// black-on-white sheet (rendered into <body> so the app shell's scroll box
// can't clip it to one page).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../lib/api';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money = (s) => (s == null || s === '' ? '' : USD.format(Number(s)));
function todayStr() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
// 'YYYY-MM-DD' → "Nov 3, 2026" without a UTC shift.
function fmtDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return s || '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtStamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return ''; }
}
async function errText(r, fallback) {
  const j = await r.json().catch(() => ({}));
  if (j.code === 'migration_missing') return 'The database migration for the "Looking for" list has not been applied yet.';
  return j.error || fallback;
}
const jsonOpts = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
const blank = () => ({ entry_date: todayStr(), notes: '', paid_from: '', amount: '' });

// ── printable sheet (portal into <body>; only visible in print) ─────────────
function PrintSheet({ tab, list, total, noAmount }) {
  const found = tab === 'found';
  const printed = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return createPortal(
    <div className="hq-v122-sheet">
      <div className="hq-v122-sheet-head">
        <div>
          <div className="hq-v122-sheet-co">XYZ Merchandise LLC</div>
          <h1>{found ? 'Receipts & invoices — found' : 'Receipts & invoices needed'}</h1>
        </div>
        <div className="hq-v122-sheet-meta">
          <div>Printed {printed}</div>
          <div>{list.length} {list.length === 1 ? 'item' : 'items'} · {money(total)}</div>
        </div>
      </div>
      <table className="hq-v122-sheet-table">
        <thead>
          <tr>
            <th className="ck">{found ? 'Found' : '✓'}</th>
            <th className="dt">Date</th>
            <th>Notes</th>
            <th className="pf">Paid from</th>
            <th className="amt">Amount</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.id}>
              <td className="ck">{found ? fmtStamp(r.found_at) : <span className="box" />}</td>
              <td className="dt">{fmtDate(r.entry_date)}</td>
              <td>{r.notes}</td>
              <td className="pf">{r.paid_from}</td>
              <td className="amt">{money(r.amount) || '—'}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={5} className="none">Nothing on this list.</td></tr>}
        </tbody>
        {list.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4} className="lbl">Total{noAmount ? ` (${noAmount} without an amount)` : ''}</td>
              <td className="amt">{money(total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>,
    document.body,
  );
}

// ── one row (view or edit) ──────────────────────────────────────────────────
function Row({ r, tab, canManage, busy, onSave, onStatus, onDelete }) {
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const mine = busy === r.id;

  async function save() {
    const ok = await onSave(r.id, edit);
    if (ok) setEdit(null);
  }
  const key = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') setEdit(null);
  };

  if (edit) {
    return (
      <tr className="hq-v122-editrow">
        <td><input type="date" className="hq-input hq-v122-in" value={edit.entry_date} onKeyDown={key}
          onChange={(e) => setEdit({ ...edit, entry_date: e.target.value })} /></td>
        <td><input className="hq-input hq-v122-in" value={edit.notes} maxLength={1000} onKeyDown={key} autoFocus
          onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></td>
        <td><input className="hq-input hq-v122-in" value={edit.paid_from} maxLength={80} list="hq-v122-pf" onKeyDown={key}
          onChange={(e) => setEdit({ ...edit, paid_from: e.target.value })} /></td>
        <td className="r"><input className="hq-input hq-v122-in hq-v122-amt" value={edit.amount} inputMode="decimal" onKeyDown={key}
          onChange={(e) => setEdit({ ...edit, amount: e.target.value })} /></td>
        <td className="hq-v122-acts">
          <button type="button" className="hq-btn hq-v107-sm" onClick={save} disabled={mine}>{mine ? 'Saving…' : 'Save'}</button>
          <button type="button" className="hq-v107-link" onClick={() => setEdit(null)} disabled={mine}>Cancel</button>
        </td>
      </tr>
    );
  }
  return (
    <tr className={mine ? 'is-busy' : ''}>
      <td className="hq-v122-date">{fmtDate(r.entry_date)}</td>
      <td className="hq-v122-notes">
        {r.notes || <span className="hq-v107-muted">—</span>}
        {tab === 'found' && <div className="hq-v107-muted hq-v107-small">Found {fmtStamp(r.found_at)}{r.found_by_name ? ' · ' + r.found_by_name : ''}</div>}
      </td>
      <td>{r.paid_from || <span className="hq-v107-muted">—</span>}</td>
      <td className="r hq-v122-money">{money(r.amount) || <span className="hq-v107-muted">—</span>}</td>
      <td className="hq-v122-acts">
        {confirmDel ? (
          <>
            <button type="button" className="hq-btn hq-v107-danger hq-v107-sm" onClick={() => onDelete(r.id)} disabled={mine}>Delete</button>
            <button type="button" className="hq-v107-link" onClick={() => setConfirmDel(false)}>Cancel</button>
          </>
        ) : (
          <>
            {tab === 'open'
              ? <button type="button" className="hq-btn hq-v107-go hq-v107-sm" onClick={() => onStatus(r.id, 'found')} disabled={mine}>Found</button>
              : <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={() => onStatus(r.id, 'open')} disabled={mine}>Reopen</button>}
            <button type="button" className="hq-v107-link" disabled={mine}
              onClick={() => setEdit({ entry_date: r.entry_date, notes: r.notes, paid_from: r.paid_from, amount: r.amount || '' })}>Edit</button>
            {canManage && <button type="button" className="hq-v107-link hq-v107-dellink" onClick={() => setConfirmDel(true)} disabled={mine}>Delete</button>}
          </>
        )}
      </td>
    </tr>
  );
}

// ── paste from a statement (@@RECEIPT_PASTE_v122_1@@) ───────────────────────
// Paste → Preview (server parses; nothing saved) → untick / tidy notes → Add.
// Lines already on the list (same date + amount + notes) start unticked.
function PastePanel({ defaultPaidFrom, onCancel, onDone }) {
  const [text, setText] = useState('');
  const [paidFrom, setPaidFrom] = useState(defaultPaidFrom || '');
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  async function preview() {
    setBusy('parse'); setErr('');
    try {
      const r = await apiFetch('/api/receipt-requests/parse', jsonOpts('POST', { text }));
      if (!r.ok) { setErr(await errText(r, 'Could not read that.')); return; }
      const j = await r.json();
      if (!j.items.length) { setErr('No lines found. Each line needs a date (like 16 Oct 2026) followed by its details and amount.'); return; }
      setPrev({ ...j, items: j.items.map((it, k) => ({ ...it, key: k, on: !it.duplicate })) });
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setBusy(''); }
  }
  async function addAll() {
    const picked = prev.items.filter((it) => it.on);
    if (!picked.length) return;
    setBusy('add'); setErr('');
    try {
      const r = await apiFetch('/api/receipt-requests/bulk', jsonOpts('POST', {
        paid_from: paidFrom,
        items: picked.map((it) => ({ entry_date: it.entry_date, notes: it.notes, amount: it.amount })),
      }));
      if (!r.ok) { setErr(await errText(r, 'Could not add those lines.')); return; }
      const j = await r.json();
      onDone(j.count);
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setBusy(''); }
  }
  const setItem = (key, p) => setPrev((v) => ({ ...v, items: v.items.map((it) => (it.key === key ? { ...it, ...p } : it)) }));
  const picked = prev ? prev.items.filter((it) => it.on) : [];
  const pickedTotal = picked.reduce((a, it) => a + (it.amount == null ? 0 : Number(it.amount)), 0);
  const allOn = prev && prev.items.length > 0 && prev.items.every((it) => it.on);
  const dups = prev ? prev.items.filter((it) => it.duplicate).length : 0;

  return (
    <div className="hq-v1221-paste">
      {!prev ? (
        <>
          <label className="hq-v107-k" htmlFor="hq-v1221-text">Paste statement lines</label>
          <textarea id="hq-v1221-text" className="hq-input hq-v1221-text" rows={9} value={text} autoFocus
            placeholder={'16 Oct 2026\nDescription: DALB INC. KEARNEYSVILLE WV\nPayee: Dalb Inc.\n$538.46\n14 Oct 2026\n…'}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) preview(); }} />
          <p className="hq-v107-muted hq-v107-small hq-v1221-hint">Each line starts at a date. Payee and Description become the notes. Nothing is saved until you add.</p>
        </>
      ) : (
        <div className="hq-v1221-prev">
          <div className="hq-v1221-prevbar">
            <span><strong>{prev.items.length}</strong> {prev.items.length === 1 ? 'line' : 'lines'} found
              {dups ? <> · <span className="hq-v1221-dup">{dups} already on the list</span></> : null}
              {prev.skipped ? <span className="hq-v107-muted"> · {prev.skipped} couldn’t be read</span> : null}
              {prev.truncated ? <span className="hq-v107-muted"> · only the first {prev.max} shown</span> : null}
            </span>
            <button type="button" className="hq-v107-link" onClick={() => setPrev(null)} disabled={!!busy}>Edit pasted text</button>
          </div>
          <div className="hq-v107-scroll hq-v1221-scroll">
            <table className="hq-table hq-v122-table hq-v1221-table">
              <thead>
                <tr>
                  <th className="hq-v107-ck"><input type="checkbox" checked={!!allOn} aria-label="Include all"
                    onChange={() => setPrev((v) => ({ ...v, items: v.items.map((it) => ({ ...it, on: !allOn })) }))} /></th>
                  <th>Date</th>
                  <th>Notes</th>
                  <th className="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {prev.items.map((it) => (
                  <tr key={it.key} className={it.on ? '' : 'is-off'}>
                    <td className="hq-v107-ck"><input type="checkbox" checked={it.on} onChange={() => setItem(it.key, { on: !it.on })} aria-label="Include line" /></td>
                    <td className="hq-v122-date">{fmtDate(it.entry_date)}</td>
                    <td>
                      <input className="hq-input hq-v122-in" value={it.notes} maxLength={1000}
                        onChange={(e) => setItem(it.key, { notes: e.target.value })} />
                      {it.duplicate && <div className="hq-v1221-dup hq-v107-small">Already on the list</div>}
                    </td>
                    <td className="r hq-v122-money">{money(it.amount) || <span className="hq-v107-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="hq-v1221-foot">
        <label className="hq-v1221-pf"><span className="hq-v107-k">Paid from (all lines)</span>
          <input className="hq-input" value={paidFrom} maxLength={80} list="hq-v122-pf" placeholder="Amex 1004, Chase checking…"
            onChange={(e) => setPaidFrom(e.target.value)} /></label>
        <div className="hq-v1221-btns">
          <button type="button" className="hq-v107-link" onClick={onCancel} disabled={!!busy}>Cancel</button>
          {!prev ? (
            <button type="button" className="hq-btn hq-v107-sm" onClick={preview} disabled={!!busy || !text.trim()}>{busy === 'parse' ? 'Reading…' : 'Preview'}</button>
          ) : (
            <button type="button" className="hq-btn hq-v107-go hq-v107-sm" onClick={addAll} disabled={!!busy || !picked.length}>
              {busy === 'add' ? 'Adding…' : `Add ${picked.length} ${picked.length === 1 ? 'line' : 'lines'}${pickedTotal ? ' · ' + money(pickedTotal.toFixed(2)) : ''}`}
            </button>
          )}
        </div>
      </div>
      {err && <p className="hq-v107-err">{err}</p>}
    </div>
  );
}

// ── panel ───────────────────────────────────────────────────────────────────
export default function ReceiptRequests() {
  const [tab, setTab] = useState('open');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(blank);
  const [adding, setAdding] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState('');
  const [printing, setPrinting] = useState(false);
  const [pasting, setPasting] = useState(false); // @@RECEIPT_PASTE_v122_1@@
  const [flash, setFlash] = useState('');
  const notesRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/receipt-requests?status=' + tab);
      if (r.ok) { setData(await r.json()); setErr(''); } else setErr(await errText(r, 'Could not load the list.'));
    } catch (e) { setErr('Could not reach the server.'); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  // Print: mount the sheet, flag <body>, print, then tear down on afterprint.
  useEffect(() => {
    if (!printing) return undefined;
    const body = document.body;
    body.classList.add('hq-v122-printing');
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    const t = setTimeout(() => { window.print(); }, 60);
    return () => { clearTimeout(t); window.removeEventListener('afterprint', done); body.classList.remove('hq-v122-printing'); };
  }, [printing]);

  const list = useMemo(() => (data && data.requests) || [], [data]);
  const counts = (data && data.counts) || {};
  const canManage = !!(data && data.can_manage);
  const options = (data && data.paid_from_options) || [];

  async function add(e) {
    e.preventDefault();
    setAdding(true); setFormErr('');
    try {
      const r = await apiFetch('/api/receipt-requests', jsonOpts('POST', form));
      if (!r.ok) { setFormErr(await errText(r, 'Could not add it.')); return; }
      // keep date + paid-from: entries usually come off the same statement
      setForm((f) => ({ ...blank(), entry_date: f.entry_date, paid_from: f.paid_from }));
      if (tab !== 'open') setTab('open'); else await load();
      if (notesRef.current) notesRef.current.focus();
    } catch (e2) { setFormErr('Could not reach the server.'); }
    finally { setAdding(false); }
  }
  async function patch(id, body) {
    setBusy(id); setErr('');
    try {
      const r = await apiFetch('/api/receipt-requests/' + id, jsonOpts('PATCH', body));
      if (!r.ok) { setErr(await errText(r, 'Could not save.')); return false; }
      await load();
      return true;
    } catch (e) { setErr('Could not reach the server.'); return false; }
    finally { setBusy(''); }
  }
  async function remove(id) {
    setBusy(id); setErr('');
    try {
      const r = await apiFetch('/api/receipt-requests/' + id, { method: 'DELETE' });
      if (!r.ok) { setErr(await errText(r, 'Could not delete.')); return; }
      await load();
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setBusy(''); }
  }

  return (
    <div className="hq-panel hq-v122">
      <div className="hq-panel-head">
        <div>
          <h2>Looking for</h2>
          <p className="hq-v122-sub">Receipts &amp; invoices we still need. Mark them found as they turn up.</p>
        </div>
        <div className="hq-v1221-headbtns">
          <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={() => { setTab('open'); setPasting(true); setFlash(''); }} disabled={pasting}>
            Paste lines
          </button>
          <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={() => setPrinting(true)} disabled={!data || printing}>
            Print list
          </button>
        </div>
      </div>

      <div className="hq-v101-toolbar">
        <div className="hq-v101-seg">
          <button type="button" className={tab === 'open' ? 'on' : ''} onClick={() => setTab('open')}>
            Looking for{counts.open != null ? ' (' + counts.open + ')' : ''}
          </button>
          <button type="button" className={tab === 'found' ? 'on' : ''} onClick={() => setTab('found')}>
            Found{counts.found != null ? ' (' + counts.found + ')' : ''}
          </button>
        </div>
        {data && list.length > 0 && (
          <span className="hq-v122-total">{list.length} {list.length === 1 ? 'item' : 'items'} · <strong>{money(data.total)}</strong></span>
        )}
      </div>

      {tab === 'open' && !pasting && (
        <form className="hq-v122-add" onSubmit={add}>
          <label><span className="hq-v107-k">Date</span>
            <input type="date" className="hq-input" value={form.entry_date} required
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></label>
          <label className="hq-v122-add-notes"><span className="hq-v107-k">Notes</span>
            <input ref={notesRef} className="hq-input" value={form.notes} maxLength={1000} placeholder="Vendor, what it was for, job #…"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <label><span className="hq-v107-k">Paid from</span>
            <input className="hq-input" value={form.paid_from} maxLength={80} list="hq-v122-pf" placeholder="Amex 1004, Chase checking…"
              onChange={(e) => setForm({ ...form, paid_from: e.target.value })} /></label>
          <label><span className="hq-v107-k">Amount</span>
            <input className="hq-input hq-v122-amt" value={form.amount} inputMode="decimal" placeholder="0.00"
              onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
          <button type="submit" className="hq-btn hq-v122-addbtn" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
          {formErr && <p className="hq-v107-err hq-v122-formerr">{formErr}</p>}
        </form>
      )}
      {tab === 'open' && pasting && (
        <PastePanel defaultPaidFrom={form.paid_from}
          onCancel={() => setPasting(false)}
          onDone={(n) => { setPasting(false); setFlash('Added ' + n + (n === 1 ? ' line.' : ' lines.')); load(); }} />
      )}
      {flash && (
        <div className="hq-v107-flash"><span>{flash}</span>
          <button type="button" className="hq-modal-x" onClick={() => setFlash('')} aria-label="Dismiss">×</button>
        </div>
      )}
      <datalist id="hq-v122-pf">{options.map((o) => <option key={o} value={o} />)}</datalist>

      {err && <p className="hq-v107-err hq-v122-err">{err}</p>}
      {!data && !err ? (
        <p className="hq-empty">Loading…</p>
      ) : data && (
        <div className="hq-v107-scroll">
          <table className="hq-table hq-v122-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Notes</th>
                <th>Paid from</th>
                <th className="r">Amount</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <Row key={r.id} r={r} tab={tab} canManage={canManage} busy={busy}
                  onSave={patch} onStatus={(id, status) => patch(id, { status })} onDelete={remove} />
              ))}
              {list.length === 0 && (
                <tr><td className="hq-empty" colSpan={5}>{tab === 'open' ? 'Nothing outstanding — add anything you’re missing above.' : 'Nothing found yet.'}</td></tr>
              )}
            </tbody>
            {list.length > 0 && (
              <tfoot>
                <tr className="hq-v122-foot">
                  <td colSpan={3}>Total{data.no_amount ? <span className="hq-v107-muted"> · {data.no_amount} without an amount</span> : null}</td>
                  <td className="r hq-v122-money">{money(data.total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {data && data.truncated && <p className="hq-v107-warn">Showing the first 1,000.</p>}

      {printing && data && <PrintSheet tab={tab} list={list} total={data.total} noAmount={data.no_amount} />}
    </div>
  );
}
